import { motion, AnimatePresence } from "framer-motion";
import { Camera, Brain, Sparkles, Heart, CheckCircle2, Loader2 } from "lucide-react";

/**
 * StreamingProgress - animated multi-step indicator while AI streams the report.
 *
 * Props:
 *  - phase: 'idle' | 'starting' | 'streaming' | 'done' | 'error'
 *  - charsStreamed: number   // optional: count of chars seen
 */
export function StreamingProgress({ phase = "idle", charsStreamed = 0 }) {
  const steps = [
    { key: "upload", Icon: Camera, label: "Czytam materiał" },
    { key: "think", Icon: Brain, label: "Analizuję wzorce" },
    { key: "write", Icon: Sparkles, label: "Piszę raport" },
    { key: "finish", Icon: Heart, label: "Składam wynik" },
  ];

  let activeIndex = 0;
  if (phase === "starting") activeIndex = 0;
  else if (phase === "streaming") {
    if (charsStreamed < 300) activeIndex = 1;
    else if (charsStreamed < 1500) activeIndex = 2;
    else activeIndex = 3;
  } else if (phase === "done") activeIndex = steps.length;
  else if (phase === "error") activeIndex = -1;

  return (
    <div className="space-y-4" data-testid="streaming-progress">
      <div className="grid grid-cols-4 gap-2">
        {steps.map((s, i) => {
          const isDone = i < activeIndex || phase === "done";
          const isActive = i === activeIndex && phase !== "done";
          const Icon = isDone ? CheckCircle2 : s.Icon;
          const color = isDone ? "#32d583" : isActive ? "#ff4458" : "#475068";
          return (
            <div key={s.key} className="flex flex-col items-center text-center">
              <motion.div
                className="relative flex h-12 w-12 items-center justify-center rounded-2xl border-2"
                animate={{
                  scale: isActive ? [1, 1.08, 1] : 1,
                  borderColor: color,
                  background: isDone
                    ? "rgba(50,213,131,0.12)"
                    : isActive
                    ? "rgba(255,68,88,0.12)"
                    : "rgba(34,38,54,0.4)",
                }}
                transition={{
                  scale: { repeat: isActive ? Infinity : 0, duration: 1.4 },
                  duration: 0.3,
                }}
              >
                {isActive ? (
                  <Loader2 className="h-5 w-5 animate-spin" style={{ color }} />
                ) : (
                  <Icon className="h-5 w-5" style={{ color }} />
                )}
                {isActive && (
                  <motion.div
                    className="pointer-events-none absolute -inset-1 rounded-2xl border-2"
                    style={{ borderColor: color }}
                    animate={{ opacity: [0.2, 0.5, 0.2] }}
                    transition={{ duration: 1.6, repeat: Infinity }}
                  />
                )}
              </motion.div>
              <div
                className="mt-2 text-[10.5px] font-semibold uppercase tracking-wider"
                style={{ color: isDone || isActive ? "#dcdde6" : "#6a6f80" }}
              >
                {s.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* Progress bar */}
      <div className="relative h-1.5 overflow-hidden rounded-full bg-[#222636]">
        <motion.div
          className="absolute inset-y-0 left-0 rounded-full"
          style={{
            background:
              "linear-gradient(90deg,#ff4458,#fd267a,#ff6036)",
          }}
          initial={{ width: 0 }}
          animate={{
            width:
              phase === "done"
                ? "100%"
                : phase === "streaming"
                ? `${Math.min(95, 15 + (charsStreamed / 1800) * 80)}%`
                : phase === "starting"
                ? "8%"
                : "0%",
          }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        />
      </div>

      <AnimatePresence>
        {phase === "streaming" && (
          <motion.p
            key="hint"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="text-center text-xs text-[#a8adbd]"
          >
            <span className="dc-gradient-text font-semibold">AI pisze</span>
            <span className="ml-1.5 inline-flex">
              <Dot delay={0} />
              <Dot delay={0.15} />
              <Dot delay={0.3} />
            </span>
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
}

function Dot({ delay = 0 }) {
  return (
    <motion.span
      className="mx-[1px] inline-block h-1 w-1 rounded-full bg-[#ff4458]"
      animate={{ y: [0, -3, 0], opacity: [0.4, 1, 0.4] }}
      transition={{ duration: 0.9, repeat: Infinity, delay }}
    />
  );
}
